import React, { useCallback, useRef } from 'react';

import { AudioFeatureType } from '../../../api/cc-smarts/models/AudioFeature';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';

const fadeOutAudio = (audio: HTMLAudioElement, originalVolume: number = 1) => {
	if (!audio) {
		return;
	}

	const fadeDuration = 2500;
	const steps = 50;
	const intervalTime = fadeDuration / steps;
	let currentStep = 0;
	// Decay constant - higher values = faster initial drop
	const decayRate = 5;

	const fadeInterval = setInterval(() => {
		currentStep++;
		const progress = currentStep / steps;
		// Exponential decay: volume drops quickly at first, then slows down
		// Formula: e^(-k * progress) where k controls decay speed
		const newVolume = originalVolume * Math.exp(-decayRate * progress);

		if (currentStep >= steps || newVolume <= 0.01) {
			audio.volume = 0;
			audio.pause();
			audio.currentTime = 0;
			audio.volume = originalVolume;
			clearInterval(fadeInterval);
		} else {
			audio.volume = Math.max(0, newVolume);
		}
	}, intervalTime);
};

type AudioBriefingChimeProps = {
	isLoading?: boolean;
	src: string;
};

export const AudioBriefingChime = ({ isLoading, src }: AudioBriefingChimeProps) => {
	const { activeFeature } = useAudioPlayerStore();
	const hasPlayedRef = useRef(false);

	const handleRef = useCallback(
		(audio: HTMLAudioElement) => {
			if (!audio) {
				return;
			}

			const playChime = async () => {
				const initialVolume = audio?.volume;

				if (isLoading) {
					hasPlayedRef.current = true;
					await audio?.play();
				} else if (hasPlayedRef.current) {
					fadeOutAudio(audio, initialVolume);
				}
			};

			playChime() as unknown as void;
		},
		[isLoading],
	);

	if (activeFeature !== AudioFeatureType.BRIEFING) {
		return null;
	}

	return <audio data-testid="chime-audio-element" src={src} ref={handleRef} />;
};
